import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  useFeedPosts,
  useExplorePosts,
  useUserPostsByType,
  useCollectionPosts,
  useTrendingPosts,
  useReviewRequests
} from "../../hooks/postQueries";
import { useLikePost } from "../../hooks/useLikePost";
import { useDeletePost } from "../../hooks/useDeletePost";
import { useReadingMode } from "../../hooks/useReadingMode";
import NovelModal from "../novel/NovelModal";
import ScriptModal from "../novel/ScriptModal";
import ReportModal from "../Modal/Reportmodal";
import { useAuth } from "../../context/AuthContext";

import { Eye, Loader2, ChevronLeft, ChevronRight, CircleAlert } from "lucide-react";
import PostHeader from "./MainFeed/PostHeader";
import ImagePost from "./MainFeed/ImagePost";
import PostActions from "./MainFeed/PostActions";
import HashtagsSection from "./MainFeed/HashtagsSection";
import CommentsModal from "./MainFeed/CommentsModal";
import LikesModal from "./MainFeed/LikesModal";
import PostOptionsModal from "./MainFeed/PostOptionsModal";
import ReviewModal from "../Modal/ReviewModal";
import { backgroundOptions } from "../../utils/postConstants";
import { FeedSkeletonList } from "../Loaders/FeedSkeleton";
import EmptyState from "./EmptyState";
import NotFound from "../../pages/NotFound";

// Add this after your imports
const styles = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;

// Helper function for content splitting
const splitContentIntoSlides = (content) => {
  if (!content) return [""];

  const MAX_LINES_PER_SLIDE = 9;
  const lines = content.split("\n");
  const slides = [];

  for (let i = 0; i < lines.length; i += MAX_LINES_PER_SLIDE) {
    const slideLines = lines.slice(i, i + MAX_LINES_PER_SLIDE);
    slides.push(slideLines.join("\n"));
  }

  const cleanedSlides = slides.map((slide) =>
    slide.startsWith("\n") ? slide.slice(1) : slide
  );

  return cleanedSlides.length > 0 ? cleanedSlides : [content];
};

// Helper function to get background class from key
const getBackgroundClass = (backgroundKey) => {
  const bgOption = backgroundOptions[backgroundKey];
  return bgOption ? bgOption.class : backgroundOptions.white.class;
};

const TextContentComponent = React.memo(
  ({
    post,
    slides,
    currentSlide,
    dragOffset,
    isDragging,
    hasMultipleSlides,
    handlers,
    handleSlideChange,
  }) => {
    // Use the color from post styling (from API)
    const isTextWhite = post.textColor === "white";
    const baseTextColor = isTextWhite ? "text-white" : "text-black";

    return (
      <div
        className={`${
          post.backgroundStyle
        } rounded-xl max-[552px]:rounded-none py-5 px-4 relative overflow-hidden transition-all duration-500 h-72 flex flex-col ${
          hasMultipleSlides ? "cursor-grab" : ""
        } ${isDragging ? "cursor-grabbing" : ""}`}
        {...(hasMultipleSlides ? handlers : {})}
        style={{
          touchAction: hasMultipleSlides ? "pan-y" : "auto",
          userSelect: isDragging ? "none" : "auto",
        }}
      >
        {/* Gradient overlay */}
        {isTextWhite && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 pointer-events-none"></div>
        )}

        {/* Slide Counter */}
        {hasMultipleSlides && (
          <div className="absolute top-3 right-3 z-20">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                isTextWhite
                  ? "bg-white/20 text-white"
                  : "bg-black/5 text-gray-600"
              }`}
            >
              {currentSlide + 1}/{slides.length}
            </span>
          </div>
        )}

        {/* LEFT ARROW */}
        {hasMultipleSlides && currentSlide > 0 && (
          <button
            onClick={() => handleSlideChange(post.id, "prev")}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20
      p-1 rounded-full transition-all duration-200
      opacity-60 hover:opacity-100 hover:scale-110
      bg-gray-200 backdrop-blur-sm"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <ChevronLeft size={18} className="text-black/50" strokeWidth={4} />
          </button>
        )}

        {/* RIGHT ARROW */}
        {hasMultipleSlides && currentSlide < slides.length - 1 && (
          <button
            onClick={() => handleSlideChange(post.id, "next")}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20
      p-1 rounded-full transition-all duration-200
      opacity-60 hover:opacity-100 hover:scale-110
      bg-gray-200 backdrop-blur-sm"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <ChevronRight size={18} className="text-black/50" strokeWidth={4} />
          </button>
        )}

        <div className="relative z-10 flex flex-col h-full select-none">
          {/* Title */}
          <h2
            className={`text-xl font-semibold ${baseTextColor} ${post.fontStyle} line-clamp-2 select-none mb-3`}
          >
            {post.title}
          </h2>

          {/* Sliding Content Container */}
          <div className="flex-1 flex flex-col relative overflow-hidden select-none">
            <div
              className="flex h-full transition-transform duration-300 ease-out"
              style={{
                transform: `translateX(calc(${
                  -currentSlide * 100
                }% + ${dragOffset}px))`,
              }}
            >
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 flex flex-col select-none"
                >
                  <pre
                    className={`font-normal ${baseTextColor} ${post.fontStyle} leading-relaxed text-[14px] flex-1 whitespace-pre-wrap font-inherit select-none`}
                  >
                    {slide}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom equality check for TextContent - only re-render if essential props change
    return (
      prevProps.post.id === nextProps.post.id &&
      prevProps.currentSlide === nextProps.currentSlide &&
      prevProps.dragOffset === nextProps.dragOffset &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.post.backgroundStyle === nextProps.post.backgroundStyle &&
      prevProps.post.fontStyle === nextProps.post.fontStyle &&
      prevProps.post.title === nextProps.post.title &&
      prevProps.post.content === nextProps.post.content &&
      prevProps.post.textColor === nextProps.post.textColor
    );
  }
);

const NovelContentComponent = React.memo(({ post, onShowNovel }) => {
  const isTextWhite = post.textColor === "white";
  const baseTextColor = isTextWhite ? "text-white" : "text-black";
  const subtitleOpacity = isTextWhite
    ? "text-white text-opacity-90"
    : "text-black text-opacity-90";

  const firstChapter = post.chapters?.[0];
  if (!firstChapter) return null;

  // Check if there are multiple chapters to show the badge
  const hasMultipleChapters = post.chapters.length > 1;

  return (
    <div
      className={`${post.backgroundStyle} rounded-xl max-[552px]:rounded-none py-5 px-4 relative overflow-hidden transition-all duration-500 hover:scale-[1.01] h-72 flex flex-col`}
    >
      {isTextWhite && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 pointer-events-none"></div>
      )}

      {hasMultipleChapters && (
        <div className="absolute top-3 right-3 z-20">
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              isTextWhite
                ? "bg-white/20 text-white"
                : "bg-black/5 text-gray-600"
            }`}
          >
            {post.chapters.length} Ch.
          </span>
        </div>
      )}

      <div className="relative z-10 flex flex-col h-full">
        <h2
          className={`text-xl font-semibold ${baseTextColor} ${post.fontStyle} line-clamp-1 mb-2`}
        >
          {post.title}
        </h2>

        <h3
          className={`text-base font-normal ${subtitleOpacity} ${post.fontStyle} line-clamp-1 mb-3`}
        >
          Chapter {firstChapter.chapterNumber}: {firstChapter.title}
        </h3>

        {/* Updated Content Area */}
        <div className="flex-1 overflow-hidden">
          <p
            className={`font-normal ${baseTextColor} ${post.fontStyle} leading-relaxed text-[14px] whitespace-pre-wrap line-clamp-6`}
          >
            {firstChapter.content}
          </p>
        </div>

        <button
          onClick={() => onShowNovel(post)}
          className={`mt-2 self-start text-sm font-normal border-b pb-0.5 transition-all ${baseTextColor} ${
            isTextWhite
              ? "border-white/60 hover:border-white"
              : "border-black/60 hover:border-black"
          }`}
        >
          Continue Reading →
        </button>
      </div>
    </div>
  );
});

const ScriptContentComponent = React.memo(
  ({ post, onShowScript }) => {
    const isTextWhite = post.textColor === "white";
    const baseTextColor = isTextWhite ? "text-white" : "text-black";
    const subtitleOpacity = isTextWhite
      ? "text-white text-opacity-90"
      : "text-black text-opacity-90";

    const firstScene = post.scenes?.[0];
    if (!firstScene) return null;

    // The line-clamp-6 logic handles truncation visually now
    const hasMultipleScenes = post.scenes.length > 1;

    return (
      <div
        className={`${post.backgroundStyle} rounded-xl max-[552px]:rounded-none py-5 px-4 relative overflow-hidden transition-all duration-500 hover:scale-[1.01] h-72 flex flex-col`}
      >
        {isTextWhite && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 pointer-events-none"></div>
        )}

        {hasMultipleScenes && (
          <div className="absolute top-3 right-3 z-20">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                isTextWhite
                  ? "bg-white/20 text-white"
                  : "bg-black/5 text-gray-600"
              }`}
            >
              {post.scenes.length} Sc.
            </span>
          </div>
        )}

        <div className="relative z-10 flex flex-col h-full">
          <h2
            className={`text-xl font-semibold ${baseTextColor} ${post.fontStyle} line-clamp-1 mb-2`}
          >
            {post.title}
          </h2>

          <h3
            className={`text-base font-normal ${subtitleOpacity} ${post.fontStyle} mb-3`}
          >
            Scene {firstScene.sceneNumber}: {firstScene.title}
          </h3>

          {/* Updated Content Area with line-clamp */}
          <div className="flex-1 overflow-hidden">
            <pre
              className={`font-normal ${baseTextColor} ${post.fontStyle} leading-relaxed text-[14px] whitespace-pre-wrap line-clamp-6`}
            >
              {firstScene.content}
            </pre>
          </div>

          {/* Updated Button Style */}
          <button
            onClick={() => onShowScript(post)}
            className={`mt-2 self-start text-sm font-normal border-b pb-0.5 transition-all ${baseTextColor} ${
              isTextWhite
                ? "border-white/60 hover:border-white"
                : "border-black/60 hover:border-black"
            }`}
          >
            Continue Reading →
          </button>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.post.id === nextProps.post.id &&
      prevProps.post.backgroundStyle === nextProps.post.backgroundStyle &&
      prevProps.post.fontStyle === nextProps.post.fontStyle &&
      prevProps.post.title === nextProps.post.title &&
      prevProps.post.textColor === nextProps.post.textColor &&
      JSON.stringify(prevProps.post.scenes) ===
        JSON.stringify(nextProps.post.scenes)
    );
  }
);

const FeedPostComponent = ({
  post,
  likedPosts,
  bookmarkedPosts,
  currentSlides,
  currentImageSlides,
  handleLike,
  handleBookmark,
  handleSlideChange,
  handleImageSlideChange,
  handleShowComments,
  handleShowLikes,
  handleShowOptions,
  handleShowNovel,
  handleShowScript,
  handleCollectionChange,
  useSwipe,
  feedType,
  onAddReview,
  hideEngagement
}) => {
  const isLiked = likedPosts.has(post.id);
  const isBookmarked = bookmarkedPosts.has(post.id);

  // Check if this is a novel/book/story (all use chapters) or script (uses scenes)
  const isChapterBased =
    (post.type === "Novel" || post.type === "Book" || post.type === "Story") &&
    post.chapters?.length > 0;
  const isScript = post.type === "Script" && post.scenes?.length > 0;

  // Text Slide Logic (only for non-novel posts)
  const slides = useMemo(() => {
    if (isChapterBased || isScript) return [];
    return splitContentIntoSlides(post.content);
  }, [post.content, isChapterBased, isScript]);

  const currentSlide = currentSlides[post.id] || 0;
  // const hasMultipleSlides = !isNovel && slides.length > 1;
  const hasMultipleSlides = !isChapterBased && !isScript && slides.length > 1;

  // Image Slide Logic
  const images = post.imageurl || [];
  const currentImageSlide = currentImageSlides[post.id] || 0;
  const hasMultipleImages = images.length > 1;

  const { isDragging, dragOffset, handlers } = useSwipe(
    post.id,
    slides,
    false,
    handleSlideChange,
    handleImageSlideChange
  );

  // Use swipe functionality for images
  const {
    isDragging: isImageDragging,
    dragOffset: imageDragOffset,
    handlers: imageHandlers,
  } = useSwipe(
    post.id,
    images,
    true,
    handleSlideChange,
    handleImageSlideChange,
    images.length
  );

  return (
    <div
      className={`bg-white hover:shadow-md transition-all duration-300 mb-0 sm:mb-6 overflow-hidden w-full mx-auto border border-transparent sm:border sm:border-gray-100 sm:rounded-xl`}
    >
      {/* Post Header */}
      <PostHeader
        post={post}
        hasMultipleSlides={hasMultipleSlides}
        currentSlide={currentSlide}
        slides={slides}
        handleSlideChange={handleSlideChange}
        hasMultipleImages={hasMultipleImages}
        currentImageSlide={currentImageSlide}
        images={images}
        handleImageSlideChange={handleImageSlideChange}
        onShowOptions={handleShowOptions}
        feedType={feedType}
        hideEngagement={hideEngagement}
      />

      {/* Post Content */}
      <div className="px-4 pb-2 feed-padding">
        {isChapterBased ? (
          <NovelContentComponent post={post} onShowNovel={handleShowNovel} />
        ) : isScript ? (
          <ScriptContentComponent post={post} onShowScript={handleShowScript} />
        ) : post.content ? (
          <TextContentComponent
            post={post}
            slides={slides}
            currentSlide={currentSlide}
            dragOffset={dragOffset}
            isDragging={isDragging}
            hasMultipleSlides={hasMultipleSlides}
            handlers={handlers}
            handleSlideChange={handleSlideChange}
          />
        ) : (
          // Render Image Post
          <ImagePost
            post={post}
            images={images}
            currentImageSlide={currentImageSlide}
            hasMultipleImages={hasMultipleImages}
            isImageDragging={isImageDragging}
            imageDragOffset={imageDragOffset}
            imageHandlers={imageHandlers}
          />
        )}
      </div>

      {feedType === "review_requests" ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onAddReview(post)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onAddReview(post);
          }}
          className="w-full -mt-2 py-1 px-4 bg-[#b0d284] border-t border-b border-black/10 cursor-pointer active:bg-[#9ec170] transition-colors flex items-center justify-center"
        >
          <span className="text-sm font-semibold text-black tracking-wide">
            Add your review
          </span>
        </div>
      ) : (
        <>
          {/* Post Actions - hidden in reading mode */}
          {!hideEngagement && (
            <PostActions
              post={post}
              isLiked={isLiked}
              isBookmarked={isBookmarked}
              handleLike={handleLike}
              handleBookmark={handleBookmark}
              onShowComments={handleShowComments}
              onShowLikes={handleShowLikes}
              onCollectionChange={handleCollectionChange}
            />
          )}

          {/* Hashtags Section */}
          <HashtagsSection author={post.author} hashtags={post.tags} />

          {/* Caption Section (only for image posts) */}
          {post.imageurl && post.imageurl.length > 0 && post.caption && (
            <div className="px-4 pb-3">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-gray-900 text-sm sm:text-base md:text-base lg:text-base flex-shrink-0">
                  {post.author}
                </span>
                <span className="text-gray-800 text-sm sm:text-base md:text-base lg:text-base leading-relaxed break-words">
                  {post.caption}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Memoize the FeedPost component
const FeedPost = React.memo(FeedPostComponent);

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
        ...options,
      }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [callback, options]);

  return targetRef;
};

const MainFeed = ({
  isExplore = false,
  isTrending = false,
  isReviewRequests = false,
  isDetailView = false,
  isCollectionDetailView = false,
  username = null,
  postId = null,
  profileID = null,
  postType = "all",
  collectionType = null,
}) => {
  if (isCollectionDetailView && postId && collectionType) {
    return (
      <CollectionDetailFeed postId={postId} collectionType={collectionType} />
    );
  }

  if (isDetailView && username && postId && profileID) {
    return (
      <PostDetailFeed
        username={username}
        postId={postId}
        profileID={profileID}
        postType={postType}
      />
    );
  }

  if (isReviewRequests) {
    return <ReviewRequestsFeed />;
  }

  if (isTrending) {
    return <TrendingFeed />;
  }

  if (isExplore) {
    return <ExploreFeed />;
  }

  return <FeedContent />;
};

const PostDetailFeed = ({ username, postId, profileID, postType }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    data: userPostsData,
    isLoading: isLoadingFeed, 
    error: feedError, 
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUserPostsByType(profileID, postType, 10, postId); 

  const isOwnProfile = user?.profile?.username === username?.replace("@", "");

  if (isLoadingFeed) {
    return (
      <main className="flex-1 pb-16">
        <div className="w-full">
          <div className="-mx-4 sm:mx-0 sm:max-w-xl sm:mx-auto space-y-0 sm:space-y-4">
            <FeedSkeletonList count={3} />
          </div>
        </div>
      </main>
    );
  }

  const hasPosts = userPostsData?.pages?.[0]?.posts?.length > 0;

  if (feedError || !hasPosts) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-4 text-center">
        {/* Icon */}
        <div className="w-24 h-24 flex items-center justify-center rounded-full border-2 border-black mb-3">
          <CircleAlert className="w-12 h-12 text-black" />
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold mb-1 text-black">
          Post not found
        </h2>

        {/* Subtext */}
        <p className="text-gray-500 mb-4 max-w-sm">
          This post may have been removed or is no longer accessible.
        </p>

        {/* Button with Navigate */}
        <button 
          onClick={() => navigate(`/${username}`)}
          className="px-6 py-2 font-semibold bg-[#b0d284] text-black hover:bg-[#9ec170] transition-all duration-200 active:scale-95 rounded-full"
        >
          See More Posts on Wryven
        </button>
      </div>
    );
  }

  return (
    <FeedDisplay
      feedPostsData={userPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}  
      feedError={feedError}
      feedType="profile_detail"
      isDetailView={true}
      isOwnProfile={isOwnProfile}
      queryKey={["userPosts", profileID, postType, postId]}
    />
  );
};

const CollectionDetailFeed = ({ postId, collectionType }) => {
  const navigate = useNavigate();

  const {
    data: feedPostsData,
    isLoading: isLoadingFeed,
    error: feedError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCollectionPosts(collectionType, 10, postId);

  // Transform and flatten posts
  const allPosts = useMemo(() => {
    if (!feedPostsData) return [];
    return feedPostsData.pages.flatMap((page) => page.posts || []);
  }, [feedPostsData]);

  if (isLoadingFeed) {
    return (
      <main className="flex-1 pb-16">
        <div className="w-full">
          <div className="-mx-4 sm:mx-0 sm:max-w-xl sm:mx-auto space-y-0 sm:space-y-4">
            <FeedSkeletonList count={3} />
          </div>
        </div>
      </main>
    );
  }

  const hasPosts = allPosts.length > 0; 

  if (feedError || !hasPosts) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-4 text-center">
        {/* Icon */}
        <div className="w-24 h-24 flex items-center justify-center rounded-full border-2 border-black mb-3">
          <CircleAlert className="w-12 h-12 text-black" />
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold mb-1 text-black">
          Post not found
        </h2>

        {/* Subtext */}
        <p className="text-gray-500 mb-4 max-w-sm">
          This post may have been removed or is no longer accessible.
        </p>

        {/* Button with Navigate */}
        <button 
          onClick={() => navigate('/explore')}
          className="px-6 py-2 font-semibold bg-[#b0d284] text-black hover:bg-[#9ec170] transition-all duration-200 active:scale-95 rounded-full"
        >
          See More Posts on Wryven
        </button>
      </div>
    );
  }

  return (
    <FeedDisplay
      feedPostsData={feedPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}
      feedError={feedError}
      feedType="collection_detail"
      isDetailView={true}
      queryKey={["collectionPosts", collectionType, postId]}
    />
  );
};


const TrendingFeed = () => {
  const { user } = useAuth();
  const {
    data: feedPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFeed,
    error: feedError,
  } = useTrendingPosts(10);

  return (
    <FeedDisplay
      feedPostsData={feedPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}
      feedError={feedError}
      feedType="trending"
      currentUser={user}
      queryKey={["trendingPosts"]}
    />
  );
};

const ReviewRequestsFeed = () => {
  const { user } = useAuth();
  const {
    data: feedPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFeed,
    error: feedError,
  } = useReviewRequests();

  return (
    <FeedDisplay
      feedPostsData={feedPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}
      feedError={feedError}
      feedType="review_requests"
      currentUser={user}
      queryKey={["reviewRequests"]}
    />
  );
};

const FeedContent = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const feedType = searchParams.get("type") || "for_you";

  const {
    data: feedPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFeed,
    error: feedError,
  } = useFeedPosts(feedType, 10);
  return (
    <FeedDisplay
      feedPostsData={feedPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}
      feedError={feedError}
      feedType={feedType}
      currentUser={user}
      queryKey={["feedPosts", feedType]}
    />
  );
};

const ExploreFeed = () => {
  const { user } = useAuth();
  const {
    data: feedPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFeed,
    error: feedError,
  } = useExplorePosts(10);

  return (
    <FeedDisplay
      feedPostsData={feedPostsData}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoadingFeed={isLoadingFeed}
      feedError={feedError}
      feedType="explore"
      currentUser={user}
      queryKey={["explorePosts"]}
    />
  );
};

const FeedDisplay = ({
  feedPostsData,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoadingFeed,
  feedError,
  feedType,
  isOwnProfile = false,
  currentUser = null,
  queryKey = ["feedPosts", "for_you"],
}) => {
  const { mutate: likePost } = useLikePost();
  const { mutate: deletePostMutation, isLoading: isDeleting } = useDeletePost();
  const [readingMode] = useReadingMode();

  const [showNovelModal, setShowNovelModal] = useState(null);
  const [showScriptModal, setShowScriptModal] = useState(null);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoadingFeed) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoadingFeed, fetchNextPage]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  const [bookmarkedPosts, setBookmarkedPosts] = useState(new Set());
  const [currentSlides, setCurrentSlides] = useState({});
  const [currentImageSlides, setCurrentImageSlides] = useState({});
  const [showComments, setShowComments] = useState(null);
  const [showLikes, setShowLikes] = useState(null);
  const [selectedPostForComments, setSelectedPostForComments] = useState(null);
  const [showOptions, setShowOptions] = useState(null);
  const [postCollections, setPostCollections] = useState({});
  const [reviewModalDraftId, setReviewModalDraftId] = useState(null);
  const [reportSuccessMsg, setReportSuccessMsg] = useState(null); 

  // Add this useEffect to inject the styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Transform fetched data to match component structure
  const allPosts = useMemo(() => {
    if (!feedPostsData) {
      return [];
    }

    const fetchedPosts = feedPostsData.pages.flatMap(
      (page) => page.posts || []
    );

    const transformed = fetchedPosts.map((post) => ({
      id: post._id,
      _id: post._id,
      title: post.title || "",
      content: post.content || "",
      chapters: post.chapters || [],
      scenes: post.scenes || [],
      author: post.author?.username,
      name: post.author?.name,
      authorProfileId: post.author?._id,
      profilePicture: post.author?.profilePicture,
      privacySettings: post.author?.privacySettings || {
        allowLikes: 1, // Default to enabled
        allowComments: 1, // Default to enabled
      },
      type: post.type || "Poetry",
      category: "",
      tags: post.hashtags || [],
      likes: parseInt(post.stats?.likes) || 0,
      comments: parseInt(post.stats?.comments) || 0,
      shares: 0,
      views: 0,
      // Use the background class from postConstants
      backgroundStyle: getBackgroundClass(post.styling?.background || "white"),
      fontStyle: post.styling?.font || "font-georgia",
      // Add text color from API
      textColor: post.styling?.color || "black",
      verified: post.author?.isVerified || false,
      imageurl:
        post.media?.filter((m) => m.type === "image").map((m) => m.url) || [],
      caption: post.caption || "",
      location: post.location || "",
      isLikedByCurrentUser: post.isLikedByCurrentUser || false,
      // collectionType: post.collectionType || null
      collectionType:
        postCollections[post._id] !== undefined
          ? postCollections[post._id]
          : post.collectionType || null,
    }));

    // setPosts(transformed);
    return transformed;
  }, [feedPostsData, postCollections]);

  const likedPosts = useMemo(() => {
    return new Set(
      allPosts
        .filter((post) => post.isLikedByCurrentUser)
        .map((post) => post.id)
    );
  }, [allPosts]);

  const handleLike = useCallback(
    (postId) => {
      // Find the post to check current like state
      const post = allPosts.find((p) => p.id === postId);
      if (!post) {
        return;
      }

      likePost({
        postId: postId,
        isCurrentlyLiked: post.isLikedByCurrentUser,
      });
    },
    [likePost, allPosts]
  );

  const handleBookmark = useCallback((postId) => {
    setBookmarkedPosts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  }, []);

  const handleShowNovel = useCallback((post) => {
    setShowNovelModal(post);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseNovel = useCallback(() => {
    setShowNovelModal(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleShowScript = useCallback((post) => {
    setShowScriptModal(post);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseScript = useCallback(() => {
    setShowScriptModal(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleSlideChange = useCallback(
    (postId, direction) => {
      setCurrentSlides((prev) => {
        const currentSlide = prev[postId] || 0;
        const post = allPosts.find((post) => post.id === postId);
        const slides = splitContentIntoSlides(post?.content || "");

        let newSlide = currentSlide;
        if (direction === "next" && currentSlide < slides.length - 1) {
          newSlide = currentSlide + 1;
        } else if (direction === "prev" && currentSlide > 0) {
          newSlide = currentSlide - 1;
        }

        return { ...prev, [postId]: newSlide };
      });
    },
    [allPosts]
  );

  const handleImageSlideChange = useCallback(
    (postId, direction, imageCount) => {
      setCurrentImageSlides((prev) => {
        const currentSlide = prev[postId] || 0;
        let newSlide = currentSlide;

        if (direction === "next" && currentSlide < imageCount - 1) {
          newSlide = currentSlide + 1;
        } else if (direction === "prev" && currentSlide > 0) {
          newSlide = currentSlide - 1;
        }

        return { ...prev, [postId]: newSlide };
      });
    },
    []
  );

  const handleShowComments = useCallback(
    (postId) => {
      const post = allPosts.find((p) => p.id === postId);
      if (!post) {
        return;
      }
      setSelectedPostForComments(post);
      setShowComments(postId);
      document.body.style.overflow = "hidden";
    },
    [allPosts]
  );

  const handleCloseComments = useCallback(() => {
    setShowComments(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleShowLikes = useCallback((postId) => {
    setShowLikes(postId);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseLikes = useCallback(() => {
    setShowLikes(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleShowOptions = useCallback(
    (postId) => {
      const post = allPosts.find((p) => p.id === postId);
      if (!post) {
        return;
      }

      if (feedType === "review_requests") {
        // Drafts have nothing to manage — dots go straight to Report.
        setShowOptions({
          postId: postId,
          isOwner: false,
          mode: "report",
          targetType: "draft",
        });
      } else {
        setShowOptions({
          postId: postId,
          isOwner: isOwnProfile,
          mode: "menu",
          targetType: "post",
        });
      }

      document.body.style.overflow = "hidden";
    },
    [allPosts, isOwnProfile]
  );

  const handleCloseOptions = useCallback(() => {
    setShowOptions(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleOpenReport = useCallback(() => {
    // Flip the existing showOptions state into "report" mode —
    // postId is already sitting there, no new state needed for it.
    setShowOptions((prev) => (prev ? { ...prev, mode: "report" } : prev));
  }, []);

  const handleReportSuccess = useCallback((message) => {
    setShowOptions(null);           // close the report sheet
    document.body.style.overflow = "unset";
    setReportSuccessMsg(message);   // show the success message only now
    setTimeout(() => setReportSuccessMsg(null), 3000);
  }, []);

  const handleDelete = useCallback(
    (postId) => {
      setShowOptions(null);

      deletePostMutation(postId);
    },
    [deletePostMutation]
  );

  const handleCollectionChange = useCallback((postId, newCollectionType) => {
    setPostCollections((prev) => ({
      ...prev,
      [postId]: newCollectionType,
    }));
  }, []);

  const handleAddReview = useCallback((post) => {
    setReviewModalDraftId(post.id);
  }, []);

  const handleCloseReviewModal = useCallback(() => {
    setReviewModalDraftId(null);
  }, []);

  const handleReviewSuccess = useCallback((draftId) => {
    // Optimistically remove the reviewed draft from the list, or refetch, as you prefer
  }, []);

  // Custom hook for swipe functionality
  const useSwipe = (postId, items, isImageSlide = false) => {
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [currentX, setCurrentX] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [velocity, setVelocity] = useState(0);
    const lastMoveTime = useRef(Date.now());
    const lastMoveX = useRef(0);

    const currentSlide = isImageSlide
      ? currentImageSlides[postId] || 0
      : currentSlides[postId] || 0;
    const maxSlide = items.length - 1;
    const imageCount = items.length;

    const SWIPE_THRESHOLD = 50;
    const VELOCITY_THRESHOLD = 0.5;
    const RESISTANCE_FACTOR = 0.3;

    const handleStart = useCallback(
      (clientX) => {
        setIsDragging(true);
        setStartX(clientX);
        setCurrentX(clientX);
        setDragOffset(0);
        setVelocity(0);
        lastMoveTime.current = Date.now();
        lastMoveX.current = clientX;
      },
      [postId]
    );

    const handleMove = useCallback(
      (clientX) => {
        if (!isDragging) return;

        const now = Date.now();
        const deltaTime = now - lastMoveTime.current;
        const deltaX = clientX - lastMoveX.current;

        if (deltaTime > 0) {
          setVelocity(deltaX / deltaTime);
        }

        lastMoveTime.current = now;
        lastMoveX.current = clientX;

        const diff = clientX - startX;
        let newOffset = diff;

        if (
          (currentSlide === 0 && diff > 0) ||
          (currentSlide === maxSlide && diff < 0)
        ) {
          newOffset = diff * RESISTANCE_FACTOR;
        }

        setCurrentX(clientX);
        setDragOffset(newOffset);
      },
      [isDragging, startX, currentSlide, maxSlide]
    );

    const handleEnd = useCallback(() => {
      if (!isDragging) return;

      setIsDragging(false);
      const diff = currentX - startX;
      const absVelocity = Math.abs(velocity);

      const shouldSwipe =
        Math.abs(diff) > SWIPE_THRESHOLD || absVelocity > VELOCITY_THRESHOLD;

      if (shouldSwipe) {
        if (diff > 0 && currentSlide > 0) {
          if (isImageSlide) {
            handleImageSlideChange(postId, "prev", imageCount);
          } else {
            handleSlideChange(postId, "prev");
          }
        } else if (diff < 0 && currentSlide < maxSlide) {
          if (isImageSlide) {
            handleImageSlideChange(postId, "next", imageCount);
          } else {
            handleSlideChange(postId, "next");
          }
        }
      }

      setDragOffset(0);
      setVelocity(0);
    }, [
      isDragging,
      currentX,
      startX,
      velocity,
      currentSlide,
      maxSlide,
      postId,
      isImageSlide,
      imageCount,
      handleSlideChange,
      handleImageSlideChange,
    ]);

    const handleTouchStart = useCallback(
      (e) => {
        if (e.touches.length === 1) {
          handleStart(e.touches[0].clientX);
        }
      },
      [handleStart]
    );

    const handleTouchMove = useCallback(
      (e) => {
        if (isDragging) {
          handleMove(e.touches[0].clientX);
        }
      },
      [handleMove, isDragging]
    );

    const handleTouchEnd = useCallback(
      (e) => {
        handleEnd();
      },
      [handleEnd]
    );

    const handleMouseDown = useCallback(
      (e) => {
        e.preventDefault();
        handleStart(e.clientX);
      },
      [handleStart]
    );

    const handleMouseMove = useCallback(
      (e) => {
        e.preventDefault();
        handleMove(e.clientX);
      },
      [handleMove]
    );

    const handleMouseUp = useCallback(
      (e) => {
        e.preventDefault();
        handleEnd();
      },
      [handleEnd]
    );

    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("mouseleave", handleMouseUp);

        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          document.removeEventListener("mouseleave", handleMouseUp);
        };
      }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return {
      isDragging,
      dragOffset,
      handlers: {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        onMouseDown: handleMouseDown,
      },
    };
  };

  // Loading state
  if (isLoadingFeed && allPosts.length === 0) {
    return (
      <main className="flex-1 pb-16">
        <div className="w-full">
          <div className="-mx-4 sm:mx-0 sm:max-w-xl sm:mx-auto space-y-0 sm:space-y-4">
            <FeedSkeletonList count={3} />
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (feedError && allPosts.length === 0) {
      return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-4 text-center">
        {/* Icon */}
        <div className="w-24 h-24 flex items-center justify-center rounded-full border-2 border-black mb-3">
          <CircleAlert className="w-12 h-12 text-black" />
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold mb-1 text-black">
          Failed to load posts
        </h2>

        {/* Subtext */}
        <p className="text-gray-500 mb-4 max-w-sm">
          We couldn’t load your posts. Tap below to retry or explore other posts!
        </p>

        {/* Button with Navigate */}
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-2 font-semibold bg-[#b0d284] text-black hover:bg-[#9ec170] transition-all duration-200 active:scale-95 rounded-full"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isLoadingFeed && allPosts.length === 0) {
    if (feedType === "review_requests") {
      return (
        <main className="flex-1 pb-16">
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
              No posts waiting for review
            </h3>
            <p className="text-gray-500 text-center max-w-sm">
              Shared posts will appear here when writers request reviews.
            </p>
          </div>
        </main>
      );
    }

    return (
      <main className="flex-1 pb-16">
        <div className="w-full">
          <div className="-mx-4 sm:mx-0 sm:max-w-xl sm:mx-auto">
            <EmptyState type={feedType} />
          </div>
        </div>
      </main>
    );
  }

  // Reading mode only applies to Feed, Explore, and Trending — not
  // review requests, profile detail, or collection detail views.
  const excludedFeedTypes = ["review_requests", "profile_detail", "collection_detail"];
  const hideEngagement = readingMode && !excludedFeedTypes.includes(feedType);

  return (
    <main className="flex-1 pb-16">
      {/* Feed Posts */}
      <div className="w-full">
        <div className="-mx-4 sm:mx-0 sm:max-w-xl sm:mx-auto space-y-0 sm:space-y-4">
          {allPosts.map((post) => (
            <FeedPost
              key={post.id}
              post={post}
              likedPosts={likedPosts}
              bookmarkedPosts={bookmarkedPosts}
              currentSlides={currentSlides}
              currentImageSlides={currentImageSlides}
              handleLike={handleLike}
              handleBookmark={handleBookmark}
              handleSlideChange={handleSlideChange}
              handleImageSlideChange={handleImageSlideChange}
              handleShowComments={handleShowComments}
              handleShowLikes={handleShowLikes}
              handleShowOptions={handleShowOptions}
              handleShowNovel={handleShowNovel}
              handleShowScript={handleShowScript}
              handleCollectionChange={handleCollectionChange}
              useSwipe={useSwipe}
              feedType={feedType}
              onAddReview={handleAddReview}
              hideEngagement={hideEngagement}
            />
          ))}
        </div>
      </div>

      {/* Add a loading overlay for deletion */}
      {isDeleting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="text-gray-700">Deleting post...</span>
          </div>
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="w-full max-w-xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">
              Loading more posts...
            </span>
          </div>
        </div>
      )}

      {/* End of Feed Message */}
      {!hasNextPage && allPosts.length > 0 && (
        <div className="w-full max-w-xl mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center text-gray-400">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Eye className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium">You're all caught up!</p>
            <p className="text-xs mt-1">No more posts to show</p>
          </div>
        </div>
      )}

      {showComments && selectedPostForComments && (
        <CommentsModal
          postId={showComments}
          postAuthorProfileId={selectedPostForComments.authorProfileId}
          onClose={handleCloseComments}
          queryKey={queryKey}
        />
      )}

      {/* Likes Modal */}
      {showLikes && (
        <LikesModal
          postId={showLikes}
          onClose={handleCloseLikes}
          initialLikesCount={
            allPosts.find((p) => p.id === showLikes)?.likes || 0
          }
        />
      )}

      {/* Post Options Modal */}
      {/* {showOptions && (
        <PostOptionsModal
          post={allPosts.find((p) => p.id === showOptions.postId)}
          onClose={handleCloseOptions}
          onDelete={handleDelete}
          onReport={handleOpenReport}   
          isOwner={showOptions.isOwner}
          isDeleting={isDeleting}
        />
      )}

      {reportTargetId && (
        <ReportModal
          targetId={reportTargetId}
          targetType="post"
          onClose={handleCloseReport}
          onSuccess={handleReportSuccess}
        />
      )}

      {reportSuccessMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-black text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg max-w-[90vw] text-center">
          {reportSuccessMsg}
        </div>
      )} */}
      {showOptions?.mode === "menu" && (
        <PostOptionsModal
          post={allPosts.find((p) => p.id === showOptions.postId)}
          onClose={handleCloseOptions}
          onDelete={handleDelete}
          onReport={handleOpenReport}
          isOwner={showOptions.isOwner}
          isDeleting={isDeleting}
        />
      )}

      {showOptions?.mode === "report" && (
        <ReportModal
          targetId={showOptions.postId}
          targetType={showOptions.targetType || "post"}
          onClose={handleCloseOptions}
          onSuccess={handleReportSuccess}
        />
      )}

      {/* Success toast — only rendered after the report sheet has closed */}
      {reportSuccessMsg && (
        <div className="fixed bottom-6 inset-x-4 z-[70] flex justify-center">
          <div className="w-full max-w-md bg-black text-white text-[13px] font-medium px-2 py-2 rounded-full shadow-lg text-center">
            {reportSuccessMsg}
          </div>
        </div>
      )}
      {showNovelModal && (
        <NovelModal post={showNovelModal} onClose={handleCloseNovel} />
      )}
      {showScriptModal && (
        <ScriptModal post={showScriptModal} onClose={handleCloseScript} />
      )}

      <ReviewModal
        isOpen={!!reviewModalDraftId}
        onClose={handleCloseReviewModal}
        draftId={reviewModalDraftId}
        onSuccess={handleReviewSuccess}
      />
    </main>
  );
};

export default MainFeed;